import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image
} from "@react-pdf/renderer";
import logo from "../../assets/UKlogo.png";

const RED = "#8F5252";
const TABLE_RED = "#A06868";
const LIGHT_RED = "#FAF7F7";

function formatDisplayDate(value) {
  if (!value) return "";
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const styles = StyleSheet.create({
  page: {
    paddingTop: 16,
    paddingBottom: 120,
    paddingHorizontal: 32,
    fontSize: 10,
    fontFamily: "Helvetica",
    color: "#111"
  },

  /* ---------- HEADER ---------- */
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12
  },
  title: {
    fontSize: 22,
    color: RED,
    fontFamily: "Helvetica-Bold",
    marginBottom: 4
  },
  meta: {
    fontSize: 9,
    fontFamily: "Helvetica-Oblique",
    color: "#444"
  },
  logo: {
    width: 90,
    height: 48,
    objectFit: "contain"
  },

  /* ---------- BILLING ---------- */
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 18
  },
  box: {
    width: "48%",
    backgroundColor: LIGHT_RED,
    padding: 12,
    borderRadius: 6
  },
  boxTitle: {
    color: RED,
    fontFamily: "Helvetica-Bold",
    marginBottom: 6,
    fontSize: 10
  },
  boxText: {
    fontSize: 9,
    marginBottom: 2
  },

  /* ---------- TABLE ---------- */
  tableHeader: {
    flexDirection: "row",
    backgroundColor: TABLE_RED,
    color: "#fff",
    paddingVertical: 8,
    paddingHorizontal: 6,
    fontFamily: "Helvetica-Bold",
    fontSize: 9
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 6,
    borderBottom: "1 solid #E5E7EB"
  },
  itemCol: { flex: 3 },
  col: {
    flex: 1,
    textAlign: "right",
    fontSize: 9
  },
  description: {
    fontSize: 8.5,
    color: "#555",
    marginTop: 2,
    fontFamily: "Helvetica-Oblique"
  },
  discountText: {
    fontSize: 8.5,
    color: RED,
    marginTop: 2,
    fontFamily: "Helvetica-Oblique"
  },

  /* ---------- TOTALS ---------- */
  totals: {
    marginTop: 22,
    alignSelf: "flex-end",
    width: "40%"
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 6,
    fontSize: 9
  },
  grandTotal: {
    marginTop: 8,
    paddingTop: 8,
    borderTop: "2 solid #000",
    fontSize: 11,
    fontFamily: "Helvetica-Bold"
  },

  /* ---------- FOOTER ---------- */
  footer: {
    position: "absolute",
    bottom: 24,
    left: 32,
    right: 32,
    paddingTop: 10,
    borderTop: "1 solid #E5E7EB"
  },
  footerTitle: {
    color: RED,
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
    marginBottom: 4
  },
  footerText: {
    fontSize: 9,
    color: "#444",
    lineHeight: 1.4
  },
  footerContact: {
    marginTop: 4,
    fontSize: 9,
    fontFamily: "Helvetica-Bold"
  },

  paymentBox: {
    marginTop: 18,
    padding: 10
  },
  paymentTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
    color: RED,
    marginBottom: 4
  },
  paymentText: {
    fontSize: 9,
    marginBottom: 2
  }
});

export default function PdfInvoice({
  invoiceNo,
  invoiceDate,
  customer,
  items,
  totals,
  payment
}) {
  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* HEADER */}
        <View style={styles.header}>
          <View>
            <Text style={styles.title}>INVOICE</Text>
            <Text style={styles.meta}>Invoice No: {invoiceNo}</Text>
            <Text style={styles.meta}>
              Invoice Date: {formatDisplayDate(invoiceDate)}
            </Text>
          </View>
          <Image src={logo} style={styles.logo} />
        </View>

        {/* BILLING */}
        <View style={styles.row}>
          <View style={styles.box}>
            <Text style={styles.boxTitle}>Supplier</Text>
            <Text style={styles.boxText}>Sports Networks UK Limited</Text>
            <Text style={styles.boxText}>9 Appold street, 6th floor,</Text>
            <Text style={styles.boxText}>London, United Kingdom (UK) - EC2A2AP</Text>
          </View>

          <View style={styles.box}>
            <Text style={styles.boxTitle}>Customer</Text>
            <Text style={styles.boxText}>{customer.name}</Text>
            <Text style={styles.boxText}>{customer.address}</Text>
          </View>
        </View>

        {/* TABLE */}
        <View style={styles.tableHeader}>
          <Text style={styles.itemCol}>Description</Text>
          <Text style={styles.col}>Qty</Text>
          <Text style={styles.col}>Unit Price (Rs.)</Text>
          <Text style={styles.col}>Amount (Rs.)</Text>
        </View>

        {items.map((item, index) => (
          <View key={index} style={styles.tableRow}>
            <View style={styles.itemCol}>
              <Text>{item.name}</Text>
              {item.description && (
                <Text style={styles.description}>{item.description}</Text>
              )}
              {(item.discount || 0) > 0 && (
                <Text style={styles.discountText}>
                  Discount: Rs. {item.discount.toLocaleString("en-GB")}
                </Text>
              )}
            </View>
            <Text style={styles.col}>{item.quantity}</Text>
            <Text style={styles.col}>Rs. {item.rate.toLocaleString("en-GB")}</Text>
            <Text style={styles.col}>Rs. {item.total.toLocaleString("en-GB")}</Text>
          </View>
        ))}

        {/* TOTALS */}
        <View style={styles.totals}>
          <View style={styles.totalRow}>
            <Text>Subtotal</Text>
            <Text>Rs. {totals.amount.toLocaleString("en-GB")}</Text>
          </View>
          {totals.discount > 0 && ( <View style={styles.totalRow}><Text>Discount</Text><Text>Rs. {totals.discount.toLocaleString("en-GB")}</Text></View>)}

          <View style={[styles.totalRow, styles.grandTotal]}>
            <Text>Total</Text>
            <Text>Rs. {totals.total.toLocaleString("en-GB")}</Text>
          </View>
        </View>

        {/* PAYMENT */}
        <View style={styles.paymentBox}>
          <Text style={styles.paymentTitle}>Service Description</Text>

          <Text style={styles.paymentText}>
          Professional services rendered in connection with football academy programme management, player development, sports con-
          sultancy and administrative services.
          </Text>
        </View>
        {/* PAYMENT */}
        <View style={styles.paymentBox}>
          <Text style={styles.paymentTitle}>Bank Details for Payment</Text>

          <Text style={styles.paymentText}>
            Account Name: Sports Networks UK Limited
          </Text>
          <Text style={styles.paymentText}>
            Account No: 00663999
          </Text>
          <Text style={styles.paymentText}>
            Sort Code: 20-32-06
          </Text>
          <Text style={styles.paymentText}>
            Bank: Barclays Bank UK PLC
          </Text>
          <Text style={styles.paymentText}>
            IBAN: GB48 BUKB 203206 00663999
          </Text>
          <Text style={styles.paymentText}>
            SWIFT/BIC: BUKBGB22
          </Text>
          <Text style={styles.paymentText}>
            Bank Address: Leicester, London LE87 2BB
          </Text>
        </View>
        <View style={styles.paymentBox}>
          <Text style={styles.paymentTitle}>Additional note</Text>

          <Text style={styles.paymentText}>
            The above INR needs to be transferred in Pounds(GBP) as per market rate.
          </Text>
        </View>
        {/* FOOTER */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerTitle}>
            Electronic Document
          </Text>

          <Text style={styles.footerText}>
            This is a system-generated document and does not require a physical signature.
          </Text>
        </View>
      </Page>
    </Document>
  );
}